import { useCallback, useEffect, useState } from 'react'
import { api, apiUrl } from '../lib/api'
import type { Session, SessionResponse } from '../lib/types'

const POLL_INTERVAL_MS = 1500
const TERMINAL = new Set(['complete', 'failed'])

/**
 * Loads a session, then keeps it fresh until the pipeline finishes.
 * Subscribes to the SSE stream (`/sessions/:id/stream`) and falls back to
 * polling `GET /sessions/:id` (build-contract §3.3 / route map §4). The client
 * renders picks in the order the backend gives — it holds NO ranking logic.
 */
export function useSession(sessionId: string | undefined) {
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchOnce = useCallback(async () => {
    if (!sessionId) return null
    try {
      const data = await api.get<SessionResponse>(`/sessions/${sessionId}`)
      setSession(data.session)
      setError(null)
      return data.session
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this session.')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let source: EventSource | null = null
    let lastStatus: string | null = null

    const stop = () => {
      if (pollTimer) clearInterval(pollTimer)
      pollTimer = null
      source?.close()
      source = null
    }

    const refresh = async () => {
      const s = await fetchOnce()
      lastStatus = s?.status ?? lastStatus
      if (!cancelled && s && TERMINAL.has(s.status)) stop()
    }

    // Initial load.
    refresh()

    // Prefer the SSE stream; re-fetch the full session on each event so we
    // always render the contract's canonical envelope (not a partial event).
    try {
      source = new EventSource(apiUrl(`/sessions/${sessionId}/stream`), {
        withCredentials: true,
      })
      const onEvent = () => {
        if (!cancelled) refresh()
      }
      source.addEventListener('status', onEvent)
      source.addEventListener('recommendation', onEvent)
      source.addEventListener('done', () => {
        if (!cancelled) refresh()
        stop()
      })
      source.onerror = () => {
        // Stream unavailable (or closed) → fall back to polling.
        source?.close()
        source = null
        if (!pollTimer && !cancelled && !TERMINAL.has(lastStatus ?? '')) {
          pollTimer = setInterval(refresh, POLL_INTERVAL_MS)
        }
      }
    } catch {
      pollTimer = setInterval(refresh, POLL_INTERVAL_MS)
    }

    return () => {
      cancelled = true
      stop()
    }
  }, [sessionId, fetchOnce])

  return { session, error, isLoading, refresh: fetchOnce, setSession }
}
